/**
 * The public URL map of the site, in one place.
 *
 * `scripts/sitemap.ts` reads this at build time, so adding a page to
 * `SITE_ROUTES` is the only step needed to get it into sitemap.xml — and
 * `scripts/vite-plugin-sitemap.ts` warns when a route in `router.tsx` appears
 * in neither list, which is how a new page avoids being silently unindexed.
 *
 * Every entry also carries the page's `<title>` and description. Those feed
 * both the prerendered HTML (`scripts/vite-plugin-html-meta.ts`, what crawlers
 * and Slack/X unfurlers read) and the client-side update on route change
 * (`src/seo/useDocumentMeta.ts`), so a page's meta is written once here rather
 * than duplicated per consumer. The copy for the public pages comes from the
 * table in docs/marketing/content-seo.md — keep the two in step.
 *
 * Blog posts are not listed here by hand: `src/blog/posts.ts` is their index,
 * and every entry in it becomes a route below, so publishing an article is one
 * edit rather than three edits that can disagree.
 */
import { BLOG_INDEX_PATH, BLOG_POSTS_BY_DATE, postPath, postShareImage } from '../blog/posts';

/** How often the page's content actually changes — a hint, not a promise. */
export type ChangeFreq = 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';

export interface PageMeta {
  /**
   * Full `<title>`, and the headline of the share card. Keep it under ~60
   * characters so search results and unfurls don't truncate it, and make it
   * distinct from every other page — a shared /docs link that says "VecTime
   * Cloud" reads as the home page.
   */
  title: string;
  /** meta description / og:description. One or two sentences, ~155 chars. */
  description: string;
  /** Keep the page out of search results. Independent of sitemap membership. */
  noindex?: boolean;
  /**
   * `og:type`. Defaults to `website`; an article says so, which is what turns a
   * shared link into a card with a byline and a date rather than a site card.
   */
  ogType?: 'website' | 'article';
  /** ISO date for `article:published_time`. Articles only. */
  publishedTime?: string;
  /** ISO date for `article:modified_time`. Articles only, and only for a real revision. */
  modifiedTime?: string;
  /**
   * Share card for this page specifically, as an absolute path.
   *
   * Unset means the site-wide card. Articles set their own: a blog where every
   * post unfurls with the same image reads as abandoned, and the card is the
   * entire preview on X, Slack and LinkedIn.
   */
  image?: string;
}

export interface SiteRoute extends PageMeta {
  /** Path as crawlers see it, leading slash included, no trailing slash. */
  path: string;
  /**
   * Page source, relative to `web/`. Its last commit date becomes `<lastmod>`;
   * outside a git checkout the tag is left out rather than guessed.
   */
  source: string;
  changefreq: ChangeFreq;
  /** 0.0–1.0. Only meaningful relative to the other entries. */
  priority: number;
}

/**
 * The hub plus one route per post. Priority sits above /privacy and below
 * /docs: an article is a real entry point — most people who arrive from a
 * search or a Reddit thread land on one — but the product pages are the ones
 * that should rank for the buying keywords.
 */
const BLOG_ROUTES: readonly SiteRoute[] = [
  {
    path: BLOG_INDEX_PATH,
    source: 'src/pages/Blog.tsx',
    changefreq: 'weekly',
    priority: 0.6,
    title: 'Forecasting Benchmarks and Notes — VecTime Cloud',
    description:
      'Benchmarks, method notes and worked examples on time-series forecasting. Every number reproducible, including the ones that went against us.',
  },
  ...BLOG_POSTS_BY_DATE.map(
    (post): SiteRoute => ({
      path: postPath(post.slug),
      source: post.source,
      // An article gets corrections and updates, not a weekly rewrite.
      changefreq: 'monthly',
      priority: 0.8,
      title: post.title,
      description: post.description,
      ogType: 'article',
      publishedTime: post.published,
      modifiedTime: post.updated,
      image: postShareImage(post),
    }),
  ),
];

/** Indexable pages, most important first. */
export const SITE_ROUTES: readonly SiteRoute[] = [
  {
    path: '/',
    source: 'src/pages/Landing.tsx',
    changefreq: 'weekly',
    priority: 1.0,
    title: 'Time Series Forecasting API — VecTime Cloud',
    description:
      "POST a time series, get a probabilistic forecast. Google's TimesFM, zero-shot. Free tier: 300 calls a month, no card.",
  },
  {
    path: '/pricing',
    source: 'src/pages/Pricing.tsx',
    changefreq: 'weekly',
    priority: 0.9,
    title: 'Pricing — VecTime Cloud Forecasting API',
    description:
      'Free tier to 500,000 calls a month. Every rate limit, quota and horizon cap listed, read straight from the API. No overage surprises.',
  },
  {
    path: '/docs',
    source: 'src/pages/Docs.tsx',
    changefreq: 'weekly',
    priority: 0.9,
    title: 'API Reference — VecTime Cloud',
    description:
      'Two endpoints, one auth header. Request schema, error codes, rate-limit headers, and curl, Python and TypeScript examples for /v1/forecast.',
  },
  {
    path: '/integrations/n8n',
    source: 'src/pages/IntegrationN8n.tsx',
    changefreq: 'monthly',
    priority: 0.8,
    title: 'Time Series Forecasting in n8n — VecTime Cloud',
    description:
      'Forecast any series inside an n8n workflow with one node: rows in, median and q10–q90 band out. Free tier, 300 forecasts a month, no card.',
  },
  ...BLOG_ROUTES,
  {
    path: '/privacy',
    source: 'src/pages/Privacy.tsx',
    changefreq: 'yearly',
    priority: 0.3,
    title: 'Privacy Policy — VecTime Cloud',
    description:
      'What VecTime Cloud collects, how forecast payloads are handled and how long they are kept, who processes data for us, and how to delete your account.',
  },
  {
    path: '/terms',
    source: 'src/pages/Terms.tsx',
    changefreq: 'yearly',
    priority: 0.3,
    title: 'Terms of Service — VecTime Cloud',
    description:
      'The terms covering VecTime Cloud accounts and API keys: acceptable use, plan changes and billing, availability, and the limits of our liability.',
  },
];

/**
 * Paths deliberately kept out of the sitemap and out of robots.txt's crawl
 * budget: the dashboard needs a session, and the auth pages are either
 * single-use links or thin forms with nothing to rank for. Prefixes — `/app`
 * covers `/app/keys` and friends.
 */
export const NOINDEX_PATHS: readonly string[] = [
  '/app',
  '/login',
  '/register',
  '/verify-email',
  '/cancel-signup',
];

/**
 * Titles for the pages above. They never want traffic, but they still want a
 * correct browser tab, a `noindex` tag, and — for the auth links people do
 * paste into chat — a card that doesn't claim to be the home page.
 */
export const PRIVATE_PAGES: readonly (PageMeta & { path: string })[] = [
  {
    path: '/login',
    title: 'Sign in — VecTime Cloud',
    description: 'Sign in to manage your API keys, watch usage and change your plan.',
    noindex: true,
  },
  {
    path: '/register',
    title: 'Create your account — VecTime Cloud',
    description: 'Create a VecTime Cloud account and get 300 forecast calls free. No credit card needed.',
    noindex: true,
  },
  {
    path: '/verify-email',
    title: 'Email verification — VecTime Cloud',
    description: 'Confirm the email address on your VecTime Cloud account.',
    noindex: true,
  },
  {
    path: '/cancel-signup',
    title: 'Cancel this signup — VecTime Cloud',
    description: 'Remove an account that was created with your email address by mistake.',
    noindex: true,
  },
  { path: '/app', title: 'Overview — VecTime Cloud', description: 'Your VecTime Cloud dashboard.', noindex: true },
  { path: '/app/keys', title: 'API keys — VecTime Cloud', description: 'Create, inspect and revoke API keys.', noindex: true },
  { path: '/app/usage', title: 'Usage — VecTime Cloud', description: 'Calls, quota and rate limits for your account.', noindex: true },
  { path: '/app/playground', title: 'Playground — VecTime Cloud', description: 'Try a forecast from the browser.', noindex: true },
  { path: '/app/billing', title: 'Plan & billing — VecTime Cloud', description: 'Change plan, see invoices and payment details.', noindex: true },
  { path: '/app/admin', title: 'Admin — VecTime Cloud', description: 'Internal administration.', noindex: true },
];

/** Meta for any URL that matches no route — the SPA's 404. */
export const NOT_FOUND_META: PageMeta = {
  title: 'Page not found — VecTime Cloud',
  description: 'That URL does not exist. It may have been renamed, or the link was wrong.',
  noindex: true,
};

/**
 * Meta for the shell document every un-prerendered URL is served — the
 * dashboard, and anything that turns out to be a 404. It claims nothing: no
 * canonical, no og:url, and `noindex`, so a mistyped link can neither be
 * indexed nor unfurl as though it were the home page. The browser tab is
 * corrected by `useDocumentMeta` as soon as the app knows the route.
 */
export const SPA_SHELL_META: PageMeta = {
  title: 'VecTime Cloud',
  description: "Time-series forecasting as an HTTP call, on Google's TimesFM foundation model.",
  noindex: true,
};
